import { useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { getToken, parseTokenClaims } from "../api";
import { Icon, type IconKey } from "../icons";
import { PRODUCTS, type Product } from "../products";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher";

// Language-neutral structure; copy lives in the `public` namespace.
const WHY_ICONS: IconKey[] = ["cpu", "bell", "lock", "blocks", "wallet", "headset"];

const MODULE_DEFS: { icon: IconKey; tone: string; key: string }[] = [
  { icon: "map", tone: "indigo", key: "tracking" },
  { icon: "eye", tone: "amber", key: "driver" },
  { icon: "fuel", tone: "emerald", key: "fuel" },
  { icon: "boxes", tone: "violet", key: "delivery" },
  { icon: "thermometer", tone: "rose", key: "border" },
];

const STAT_KEYS = ["eventLatency", "channels", "offline", "perFirm", "obd", "api"];

const ARAC_MARKALARI = [
  "Scania",
  "MAN",
  "Mercedes-Benz",
  "Volvo",
  "Renault",
  "Iveco",
  "DAF",
  "Ford",
];

const TOPIC_KEYS = ["", "secure", "driver", "delivery", "clerk", "border", "platform"];

function ProductCard({ p }: { p: Product }) {
  const { t } = useTranslation("public");
  return (
    <article className={`product-card tone-${p.tone}`}>
      <div className="product-icon">
        <Icon name={p.icon} />
      </div>
      <div className="product-brand">{p.brand}</div>
      <div className="product-tagline">{t(`products.${p.slug}.tagline`)}</div>
      <p className="product-body">{t(`products.${p.slug}.hero`)}</p>
      <Link to={`/products/${p.slug}`} className="product-link">
        {t("solutions.detailLink")} <span aria-hidden>&rarr;</span>
      </Link>
    </article>
  );
}

interface LeadFormState {
  name: string;
  phone: string;
  email: string;
  topic: string;
  message: string;
  consent: boolean;
}

function DemoForm() {
  const { t } = useTranslation("public");
  const [form, setForm] = useState<LeadFormState>({
    name: "",
    phone: "",
    email: "",
    topic: "",
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");

  const update = <K extends keyof LeadFormState>(field: K, value: LeadFormState[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.consent) {
      setErrorMsg(t("demo.form.consentRequired"));
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch("/api/v1/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          topic: form.topic || null,
          message: form.message,
        }),
      });
      if (!res.ok) {
        throw new Error(t("demo.form.requestFailed", { status: res.status }));
      }
      setStatus("sent");
      setForm({
        name: "",
        phone: "",
        email: "",
        topic: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("demo.form.unknownError"));
      setStatus("error");
    }
  };

  return (
    <form className="demo-form" onSubmit={submit}>
      <div className="demo-form-row">
        <label>
          <span>{t("demo.form.name")}</span>
          <input
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder={t("demo.form.namePlaceholder")}
          />
        </label>
        <label>
          <span>{t("demo.form.phone")}</span>
          <input
            required
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="0 (5XX) XXX XX XX"
            type="tel"
          />
        </label>
      </div>
      <div className="demo-form-row">
        <label>
          <span>{t("demo.form.email")}</span>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="ad@firma.com"
          />
        </label>
        <label>
          <span>{t("demo.form.topic")}</span>
          <select
            value={form.topic}
            onChange={(e) => update("topic", e.target.value)}
          >
            {TOPIC_KEYS.map((key) => (
              <option key={key} value={key}>
                {key === "" ? t("demo.topics.placeholder") : t(`demo.topics.${key}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        <span>{t("demo.form.message")}</span>
        <textarea
          rows={4}
          required
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
          placeholder={t("demo.form.messagePlaceholder")}
        />
      </label>
      <label className="demo-consent">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => update("consent", e.target.checked)}
        />
        <span>
          <Trans
            t={t}
            i18nKey="demo.form.consent"
            components={[<Link to="/legal/kvkk" />]}
          />
        </span>
      </label>
      <button type="submit" disabled={status === "sending"} className="btn primary lg">
        {status === "sending" ? t("demo.form.submitting") : t("demo.form.submit")}{" "}
        <span aria-hidden>&rarr;</span>
      </button>
      {status === "sent" && <p className="demo-success">{t("demo.form.success")}</p>}
      {status === "error" && <p className="demo-error">{errorMsg}</p>}
    </form>
  );
}

export function LandingPage() {
  const { t } = useTranslation("public");
  const loggedIn = !!getToken();
  const claims = parseTokenClaims();
  const panelLabel = loggedIn
    ? claims?.role === "ADMIN"
      ? t("nav.adminPanel")
      : t("nav.panel")
    : t("nav.login");
  const panelTarget = loggedIn ? "/overview" : "/login";

  return (
    <div className="landing">
      <header className="landing-nav">
        <Link to="/" className="brand" aria-label="AICE">
          <img src="/logo-mark.png" alt="AICE — AiCruise Eye" className="brand-logo" />
        </Link>
        <nav>
          <a href="#cozumler">{t("nav.solutions")}</a>
          <a href="#moduller">{t("nav.modules")}</a>
          <a href="#neden">{t("nav.why")}</a>
          <a href="#iletisim">{t("nav.contact")}</a>
          <Link to={panelTarget} className="link-ghost">
            {panelLabel}
          </Link>
          <a href="#demo" className="cta">
            {t("nav.demo")} <span aria-hidden>&rarr;</span>
          </a>
          <LanguageSwitcher />
        </nav>
      </header>

      <section className="hero">
        <div className="hero-bg" aria-hidden>
          <div className="hero-grid" />
          <div className="hero-glow hero-glow-1" />
          <div className="hero-glow hero-glow-2" />
        </div>
        <div className="hero-inner">
          <div className="hero-text">
            <span className="eyebrow">{t("hero.eyebrow")}</span>
            <h1>
              {t("hero.titleA")}
              <span className="accent">{t("hero.titleAccent1")}</span>
              {t("hero.titleB")}
              <span className="accent">{t("hero.titleAccent2")}</span>
              {t("hero.titleC")}
            </h1>
            <p className="lead">{t("hero.lead")}</p>
            <div className="hero-actions">
              <a href="#demo" className="btn primary lg">
                {t("hero.demoBtn")} <span aria-hidden>&rarr;</span>
              </a>
              <a href="#cozumler" className="btn ghost lg">
                {t("hero.exploreBtn")}
              </a>
            </div>
            <ul className="hero-trust" aria-label={t("hero.eyebrow")}>
              {(t("hero.trust", { returnObjects: true }) as string[]).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="hero-truck">
            <img src="/hero-track.jpg" alt={t("hero.truckAlt")} />
            <div className="hero-truck-overlay">
              <div className="hero-status">
                <div className="status-dot" /> {t("hero.statusLive")}
              </div>
              <div className="hero-truck-meta">
                <div>
                  <div className="hero-truck-num">{t("hero.stat1Num")}</div>
                  <div className="hero-truck-lbl">{t("hero.stat1Lbl")}</div>
                </div>
                <div>
                  <div className="hero-truck-num">{t("hero.stat2Num")}</div>
                  <div className="hero-truck-lbl">{t("hero.stat2Lbl")}</div>
                </div>
                <div>
                  <div className="hero-truck-num">{t("hero.stat3Num")}</div>
                  <div className="hero-truck-lbl">{t("hero.stat3Lbl")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="cozumler" className="section">
        <div className="section-head">
          <span className="eyebrow dark">{t("solutions.eyebrow")}</span>
          <h2>{t("solutions.title")}</h2>
          <p className="section-lead">{t("solutions.lead")}</p>
        </div>
        <div className="product-grid">
          {PRODUCTS.map((p) => (
            <ProductCard key={p.slug} p={p} />
          ))}
        </div>
      </section>

      <section className="stats-strip">
        <div className="stats-inner">
          {STAT_KEYS.map((key) => (
            <div key={key} className="stat">
              <div className="stat-value">{t(`stats.${key}Value`)}</div>
              <div className="stat-label">{t(`stats.${key}Label`)}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="moduller" className="section alt">
        <div className="section-head">
          <span className="eyebrow dark">{t("modules.eyebrow")}</span>
          <h2>{t("modules.title")}</h2>
          <p className="section-lead">{t("modules.lead")}</p>
        </div>
        <div className="module-grid">
          {MODULE_DEFS.map((m) => (
            <article key={m.key} className={`module-card tone-${m.tone}`}>
              <div className="module-icon">
                <Icon name={m.icon} />
              </div>
              <h3>{t(`modules.${m.key}.title`)}</h3>
              <p>{t(`modules.${m.key}.body`)}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="neden" className="section">
        <div className="section-head">
          <span className="eyebrow dark">{t("why.eyebrow")}</span>
          <h2>{t("why.title")}</h2>
        </div>
        <div className="neden-grid">
          {(t("why.items", { returnObjects: true }) as { title: string; body: string }[]).map(
            (n, i) => (
              <div key={n.title} className="neden-item">
                <div className="neden-icon">
                  <Icon name={WHY_ICONS[i] ?? "cpu"} />
                </div>
                <h3>{n.title}</h3>
                <p>{n.body}</p>
              </div>
            ),
          )}
        </div>
      </section>

      <section className="brand-strip" aria-label={t("brandStrip.label")}>
        <div className="brand-strip-inner">
          <div className="brand-strip-label">{t("brandStrip.label")}</div>
          <div className="brand-strip-row">
            {ARAC_MARKALARI.map((b) => (
              <span key={b} className="brand-name">
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="demo" className="section demo-section">
        <div className="demo-grid">
          <div className="demo-info">
            <span className="eyebrow dark">{t("demo.eyebrow")}</span>
            <h2>{t("demo.title")}</h2>
            <p className="section-lead">{t("demo.lead")}</p>
            <ul className="demo-contact" id="iletisim">
              <li>
                <Icon name="phone" /> <a href="tel:+902122345567">+90 (212) 123 45 67</a>
              </li>
              <li>
                <Icon name="mail" /> <a href="mailto:info@aicruiseeye.com">info@aicruiseeye.com</a>
              </li>
              <li>
                <Icon name="pin" /> {t("demo.contactLocation")}
              </li>
              <li>
                <Icon name="headset" /> {t("demo.contactSupport")}
              </li>
            </ul>
          </div>
          <div className="demo-form-wrap">
            <DemoForm />
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-grid">
          <div className="footer-brand">
            <img src="/logo-mark.png" alt="AICE" />
            <div>
              <strong>AiCruise Eye 360</strong>
              <span className="muted">{t("footer.tagline")}</span>
            </div>
            <p className="footer-blurb">{t("footer.blurb")}</p>
          </div>
          <div className="footer-col">
            <h4>{t("footer.solutions")}</h4>
            <ul>
              {PRODUCTS.map((p) => (
                <li key={p.slug}>
                  <Link to={`/products/${p.slug}`}>{p.brand}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="footer-col">
            <h4>{t("footer.corporate")}</h4>
            <ul>
              <li>
                <a href="#neden">{t("nav.why")}</a>
              </li>
              <li>
                <a href="#moduller">{t("nav.modules")}</a>
              </li>
              <li>
                <a href="#demo">{t("nav.contact")}</a>
              </li>
              <li>
                <Link to={panelTarget}>{panelLabel}</Link>
              </li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>{t("footer.contact")}</h4>
            <ul>
              <li>
                <Icon name="phone" size={14} /> +90 (212) 123 45 67
              </li>
              <li>
                <Icon name="mail" size={14} /> info@aicruiseeye.com
              </li>
              <li>
                <Icon name="pin" size={14} /> Bursa / TR
              </li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>{t("footer.rights", { year: new Date().getFullYear() })}</span>
          <div className="footer-legal">
            <Link to="/legal/kvkk">KVKK</Link>
            <Link to="/legal/cookies">{t("legal.cookiesTitle")}</Link>
            <Link to="/legal/privacy">{t("legal.privacyTitle")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
